import { createContext, useContext } from 'react';

/**
 * Is this render `src/prerender/renderRoute.tsx`'s `renderToStaticMarkup`
 * pass, rather than a real mount (browser or test)?
 *
 * Mirrors `HubFrameContext`'s shape on purpose: a pure context so any
 * component can opt in without importing Firebase or a router, defaulting
 * `false` so every real mount and every existing test is unaffected; only
 * the prerender entry point ever provides `true`.
 *
 * WHY THIS EXISTS (THE-1134 review on PR #863). `renderToStaticMarkup` never
 * runs an effect, so a component waiting on `onAuthStateChanged` /
 * `onSnapshot` to settle (ComingUpStrip's two arms, both gated behind
 * exactly that) sits at its pre-subscription state FOREVER in that render —
 * not "still loading", but "will never hear back". A component cannot tell
 * that apart from a real mount's still-loading state by its own data alone
 * (both look like `hydrated: false`), so it needs this explicit signal from
 * the one caller that actually knows which situation it is.
 *
 * `typeof window === 'undefined'` would detect the same thing without this
 * context, but it ties the behavior to running under plain Node rather than
 * to *being this specific render* — and this repo's Jest setup
 * (`src/setupTests.js`) touches `window` unconditionally, so a `node`
 * `testEnvironment` docblock cannot be used here to exercise that branch in
 * a test. An explicit context is both narrower (only `renderRoute.tsx` can
 * ever provide it) and testable (any suite can wrap a render in the
 * provider directly).
 */
export const StaticRenderContext = createContext(false);
export const useIsStaticRender = () => useContext(StaticRenderContext);
